/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { getPendingSams } from '@/services/pending-sams'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import PendingSamCard from '@/components/cards/PendingSamCard'
import {
  getPendingSamCardProps,
  getPendingSamTableProps,
  pendingSamsListColumns,
  tableInitialState
} from './handlers'
import ItemsListWithToggleLayout from '@/components/wrappers/general/ItemsListWithToggleLayout'
import { YOUR_PENDING_SAMS } from '@/configs/strings/pending-sams'
import BuildSamModal from './components/BuildSamModal'
import PendingSamsBodyTable from './components/PendingSamsBodyTable'
import DefaultTableHidingColumns from '@/components/tables/DefaultTableHidingColumns'

const PendingSams = () => {
  const [modalIsVisible, setModalIsVisible] = useState(false)
  const [aiMetadataIds, setAiMetadataIds] = useState()
  const [isProcessing, setIsProcessing] = useState(false)
  const {
    itemsResponse,
    loading,
    onChangePage,
    page,
    searchParams,
    reloadItems,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getPendingSams
  })
  const onOpenBuildSam = async (aiMetadataIds) => {
    setAiMetadataIds(aiMetadataIds)
    setModalIsVisible(true)
  }
  const closeBuildSamModal = async (success) => {
    setModalIsVisible(false)
    setAiMetadataIds(undefined)
    success && await reloadItems()
  }
  const pendingSamsCards = itemsResponse?.data.map(pendingSams => {
    const cardProps = getPendingSamCardProps(pendingSams)

    return (
      <PendingSamCard
        key={cardProps.key}
        onAction={() => onOpenBuildSam(cardProps.aiMetadataIds)}
        {...cardProps}
      />
    )
  })

  const customTableBody = ({ table, getItemProps }) => (
    <PendingSamsBodyTable
      table={table}
      getItemProps={getItemProps}
      onOpenBuildSam={onOpenBuildSam}
    />
  )

  const table = <DefaultTableHidingColumns
    columns={pendingSamsListColumns}
    getItemProps={getPendingSamTableProps}
    onAction={onOpenBuildSam}
    data={itemsResponse?.data}
    customTableBody={customTableBody}
    tableInitialState={tableInitialState}
    visibleColumnsParamKey="visibleColumns"
  />

  return (
    <RegularTemplate
      title={YOUR_PENDING_SAMS}
      loadingContent={loading}
    >
    <ItemsListWithToggleLayout
      cards={pendingSamsCards}
      currentPage={+page}
      isLoading={loading}
      itemsLayout={searchParams.itemsLayout}
      onChangePage={onChangePage}
      searchParams={searchParams}
      table={table}
      totalItems={itemsResponse?.total}
      totalPages={totalPages}
    />
    { modalIsVisible &&
      <BuildSamModal
        active={true}
        aiMetadataIdsSelected={aiMetadataIds}
        closeModal={closeBuildSamModal}
        isProcessing={isProcessing}
        reloadItems={reloadItems}
        setIsProcessing={setIsProcessing}
      />
    }
  </RegularTemplate>
  )
}

export default PendingSams
